/**
 * Authentication and customer management module
 */
import { validateClient } from '../services/api.js';
import { handleApiError } from '../services/errorHandler.js';
import { toastify } from '../helpers/toastify.js';
import { log } from '../utils/log.js';
import { setClientId, getClientId, clearSession } from '../state.js';
import { clearSensitiveData } from '../services/storage.js';
import { ERROR_MESSAGES } from '../constants.js';

/**
 * DOM element references (initialized on setup)
 */
let customerNumberInput = null;
let validateCustomerBtn = null;
let customerInfoDiv = null;
let dossierInfoDiv = null;

/**
 * Temporary storage for pending client confirmation
 */
let pendingClientData = null;

/**
 * Initializes the auth module with DOM references
 * @param {object} elements - DOM element references
 */
export function initAuth(elements) {
  customerNumberInput = elements.customerNumberInput;
  validateCustomerBtn = elements.validateCustomerBtn;
  customerInfoDiv = elements.customerInfoDiv;
  dossierInfoDiv = elements.dossierInfoDiv;

  // Set up initial event listener
  validateCustomerBtn?.addEventListener('click', handleValidateCustomer);
}

/**
 * Handles customer validation
 */
export async function handleValidateCustomer() {
  const clientId = customerNumberInput?.value?.trim();

  if (!clientId) {
    toastify(ERROR_MESSAGES.EMPTY_CLIENT_ID, { variant: 'danger' });
    return;
  }

  try {
    const client = await validateClient(clientId);

    // Build welcome message
    let welcomeMessage = `Bonjour ${client.client_nom}`;
    if (client.client_num_tva) {
      welcomeMessage += ` (${client.client_num_tva})`;
    }

    // Update UI
    if (customerInfoDiv) {
      customerInfoDiv.textContent = welcomeMessage;
    }

    if (customerNumberInput) {
      customerNumberInput.value = clientId;
      customerNumberInput.readOnly = true;
    }

    // Update button state
    if (validateCustomerBtn) {
      validateCustomerBtn.textContent = 'Modifier';
      validateCustomerBtn.classList.remove('btn-primary');
      validateCustomerBtn.classList.add('btn-warning');
      validateCustomerBtn.removeEventListener('click', handleValidateCustomer);
      validateCustomerBtn.addEventListener('click', handleEditCustomer);
    }

    // Store client ID
    setClientId(clientId);

    log.info(`Customer validated: ${clientId}`);
  } catch (error) {
    handleApiError(error, 'Customer Validation');
    if (customerInfoDiv) {
      customerInfoDiv.textContent = '';
    }
  }
}

/**
 * Handles edit customer button click
 */
export function handleEditCustomer() {
  if (!confirm(ERROR_MESSAGES.CONFIRM_EDIT_CUSTOMER)) {
    return;
  }

  // Reset input state
  if (customerNumberInput) {
    customerNumberInput.readOnly = false;
  }

  if (customerInfoDiv) {
    customerInfoDiv.textContent = '';
  }

  // Reset button state
  if (validateCustomerBtn) {
    validateCustomerBtn.textContent = 'Valider';
    validateCustomerBtn.classList.remove('btn-warning');
    validateCustomerBtn.classList.add('btn-primary');
    validateCustomerBtn.removeEventListener('click', handleEditCustomer);
    validateCustomerBtn.addEventListener('click', handleValidateCustomer);
  }

  // Clear stored data
  clearSession();

  if (dossierInfoDiv) {
    dossierInfoDiv.textContent = '';
  }

  log.info('Customer edit mode enabled');
}

/**
 * Fetches and displays customer info
 * @param {string} clientId - Client ID
 */
export async function fetchAndDisplayCustomerInfo(clientId) {
  if (!clientId) {
    if (customerInfoDiv) {
      customerInfoDiv.textContent = '';
    }
    return;
  }

  try {
    const client = await validateClient(clientId);

    let welcomeMessage = `Bonjour ${client.client_nom}`;
    if (client.client_num_tva) {
      welcomeMessage += ` (${client.client_num_tva})`;
    }

    if (customerInfoDiv) {
      customerInfoDiv.textContent = welcomeMessage;
    }
  } catch (error) {
    log.error('Error fetching customer info:', error);
    if (customerInfoDiv) {
      customerInfoDiv.textContent = ERROR_MESSAGES.CLIENT_NOT_FOUND;
    }
    toastify(ERROR_MESSAGES.NETWORK_ERROR, { variant: 'danger' });
  }
}

/**
 * Updates customer display with stored client ID
 */
export function updateCustomerDisplay() {
  const clientId = getClientId();

  if (clientId && customerNumberInput) {
    customerNumberInput.value = clientId;
    fetchAndDisplayCustomerInfo(clientId);
  } else if (customerNumberInput) {
    customerNumberInput.value = '';
    if (customerInfoDiv) {
      customerInfoDiv.textContent = '';
    }
  }
}

/**
 * Sets the editing state for customer input
 * @param {boolean} editing - Whether editing is enabled
 * @param {HTMLElement} editValidateBtn - Edit/validate button
 */
export function setEditingState(editing, editValidateBtn) {
  if (customerNumberInput) {
    customerNumberInput.readOnly = !editing;
  }

  if (editValidateBtn) {
    if (editing) {
      editValidateBtn.textContent = 'Valider';
      editValidateBtn.classList.remove('btn-warning');
      editValidateBtn.classList.add('btn-primary');
      customerNumberInput?.focus();
      customerNumberInput?.select();
    } else {
      editValidateBtn.textContent = 'Modifier';
      editValidateBtn.classList.remove('btn-primary');
      editValidateBtn.classList.add('btn-warning');
    }
  }

  return editing;
}

/**
 * Handles client ID submission from modal - validates and shows confirmation
 * @param {string} newClientId - New client ID
 * @param {object} elements - DOM elements for the modal flow
 * @returns {Promise<boolean>} - True if validation succeeded and confirmation shown
 */
export async function handleClientIdSubmit(newClientId, elements) {
  const { errorEl, clientIdStep, confirmStep, confirmNameEl, confirmVatEl } = elements;
  const trimmedId = newClientId?.trim();

  if (!trimmedId) {
    if (errorEl) {
      errorEl.hidden = false;
    }
    return false;
  }

  if (errorEl) {
    errorEl.hidden = true;
  }

  try {
    // Validate client with API
    const client = await validateClient(trimmedId);

    // Store pending data for confirmation
    pendingClientData = {
      clientId: trimmedId,
      clientName: client.client_nom,
      clientVat: client.client_num_tva
    };

    // Show confirmation step
    if (confirmNameEl) {
      confirmNameEl.textContent = client.client_nom || 'Client';
    }
    if (confirmVatEl) {
      confirmVatEl.textContent = client.client_num_tva || '';
    }

    if (clientIdStep) {
      clientIdStep.hidden = true;
    }
    if (confirmStep) {
      confirmStep.hidden = false;
    }

    log.info(`Client validated, awaiting confirmation: ${trimmedId}`);
    return true;
  } catch (error) {
    handleApiError(error, 'Client Validation');
    return false;
  }
}

/**
 * Handles confirmation "Yes" - grants access to the app
 * @param {object} elements - DOM elements
 * @returns {Promise<boolean>} - True if access granted
 */
export async function handleConfirmYes(elements) {
  const { modal, mainContent } = elements;

  if (!pendingClientData) {
    log.error('No pending client data for confirmation');
    return false;
  }

  const currentClientId = getClientId();

  // Clear old data if client changed
  if (currentClientId && pendingClientData.clientId !== currentClientId) {
    await clearSensitiveData();
    toastify('Ancien données client effacées.');
  }

  // Save the confirmed client
  setClientId(pendingClientData.clientId);

  if (modal) {
    modal.hidden = true;
  }

  if (mainContent) {
    mainContent.hidden = false;
  }

  log.info(`Client confirmed: ${pendingClientData.clientId}`);

  // Clear pending data
  pendingClientData = null;

  return true;
}

/**
 * Handles confirmation "No" - returns to client ID input
 * @param {object} elements - DOM elements
 */
export function handleConfirmNo(elements) {
  const { clientIdStep, confirmStep, clientIdInput } = elements;

  // Clear pending data
  pendingClientData = null;

  // Return to step 1
  if (confirmStep) {
    confirmStep.hidden = true;
  }
  if (clientIdStep) {
    clientIdStep.hidden = false;
  }

  // Clear and focus input
  if (clientIdInput) {
    clientIdInput.value = '';
    clientIdInput.focus();
  }

  log.info('Client confirmation cancelled, returning to input');
}
